import React, { useEffect, useState } from "react";

import ProfilePhotoSection from "./ProfilePhotoSection";
import PersonalInfoSection from "./PersonalInfoSection";
import ContactSection from "./ContactSection";
import PrivacySection from "./PrivacySection";
import SocialLinksSection from "./SocialLinksSection";
import ProfileSlugSection from "./ProfileSlugSection";
import SubmitSection from "./SubmitSection";
import Loader from "../../Loader";
import { normalizeHandleInput, validateHandle } from "./handleValidation";

const PHONE_PREFIX = "+380";

const EMPTY_FORM = {
  user_image: null,
  first_name: "",
  last_name: "",
  description: "",
  phone_number: PHONE_PREFIX,
  is_phone_public: false,
  // Product default is opt-out (true) — matches backend RiestroUser default.
  is_presence_public: true,
  facebook: "",
  instagram: "",
  youtube: "",
  handle: "",
};

function ensurePhonePrefix(value, prefix) {
  const raw = (value || "").trim();
  if (raw === "" || raw === prefix) return prefix;
  if (raw.startsWith(prefix)) return raw;

  const digitsOnly = raw.replace(/\D/g, "").replace(/^380/, "");
  return prefix + digitsOnly;
}

const fromUser = (user) => {
  if (!user) return EMPTY_FORM;
  return {
    user_image: null,
    first_name: user.first_name || "",
    last_name: user.last_name || "",
    description: user.description || "",
    phone_number: ensurePhonePrefix(user.phone_number || "", PHONE_PREFIX),
    is_phone_public: Boolean(user.is_phone_public),
    is_presence_public: user.is_presence_public !== false,
    facebook: user.facebook || "",
    instagram: user.instagram || "",
    youtube: user.youtube || "",
    handle: user.handle || "",
  };
};

function EditProfileForm({
  initialData,
  onSubmit,
  submitting = false,
  onboardingMode = false,
  photoUploadStatus,
  serverHandleError = null,
  serverFieldErrors = null,
}) {
  const [formData, setFormData] = useState(() => fromUser(initialData));
  const [handleError, setHandleError] = useState(null);
  const [showHandleError, setShowHandleError] = useState(false);
  const [dismissedServerHandleError, setDismissedServerHandleError] = useState(false);
  const [dismissedServerFields, setDismissedServerFields] = useState({});
  const [photoCropActive, setPhotoCropActive] = useState(false);

  useEffect(() => {
    setFormData(fromUser(initialData));
    setHandleError(null);
    setShowHandleError(false);
    setDismissedServerHandleError(false);
    setDismissedServerFields({});
  }, [initialData]);

  useEffect(() => {
    // New server error arrives — surface it until the user edits handle.
    setDismissedServerHandleError(false);
  }, [serverHandleError]);

  useEffect(() => {
    setDismissedServerFields({});
  }, [serverFieldErrors]);

  useEffect(() => {
    if (photoUploadStatus?.success) {
      setFormData((prev) => ({ ...prev, user_image: null }));
    }
  }, [photoUploadStatus?.success]);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (
      field === "first_name"
      || field === "last_name"
      || field === "description"
    ) {
      setDismissedServerFields((prev) => (
        prev[field] ? prev : { ...prev, [field]: true }
      ));
    }
  };

  const normalizePhoneNumber = () =>
    setFormData((prev) => ({
      ...prev,
      phone_number: ensurePhonePrefix(prev.phone_number, PHONE_PREFIX),
    }));

  const handleHandleChange = (value) => {
    const normalized = normalizeHandleInput(value);
    updateField("handle", normalized);
    setDismissedServerHandleError(true);
    if (showHandleError) {
      setHandleError(validateHandle(normalized));
    } else {
      setHandleError(null);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (submitting || photoCropActive) return;
    const nextHandle = normalizeHandleInput(formData.handle).trim();
    const error = validateHandle(nextHandle);
    if (error) {
      setHandleError(error);
      setShowHandleError(true);
      return;
    }
    setHandleError(null);
    setShowHandleError(false);
    onSubmit?.({ ...formData, handle: nextHandle });
  };

  const displayHandleError =
    handleError
    || (!dismissedServerHandleError && (
      serverHandleError || serverFieldErrors?.handle || null
    ))
    || null;
  const displayFieldError = (field) => (
    (!dismissedServerFields[field] && serverFieldErrors?.[field]) || null
  );

  return (
    <form
      id="edit-profile-form"
      className="ap-form"
      noValidate
      data-form="edit-profile"
      onSubmit={handleSubmit}
    >
      <ProfilePhotoSection
        selectedFile={formData.user_image}
        existingImageUrl={initialData?.user_image}
        uploadStatus={photoUploadStatus}
        onUserImageChange={(file) => updateField("user_image", file)}
        onCropActiveChange={setPhotoCropActive}
      />

      <PersonalInfoSection
        firstName={formData.first_name}
        lastName={formData.last_name}
        description={formData.description}
        onFirstNameChange={(value) => updateField("first_name", value)}
        onLastNameChange={(value) => updateField("last_name", value)}
        onDescriptionChange={(value) => updateField("description", value)}
        firstNameError={displayFieldError("first_name")}
        lastNameError={displayFieldError("last_name")}
        descriptionError={displayFieldError("description")}
      />

      <ContactSection
        phoneNumber={formData.phone_number}
        onPhoneNumberChange={(value) => updateField("phone_number", value)}
        onPhoneNumberNormalize={normalizePhoneNumber}
        isPhonePublic={formData.is_phone_public}
        onIsPhonePublicChange={(value) => updateField("is_phone_public", value)}
      />

      <PrivacySection
        isPresencePublic={formData.is_presence_public}
        onIsPresencePublicChange={(value) => updateField("is_presence_public", value)}
      />

      <SocialLinksSection
        facebook={formData.facebook}
        instagram={formData.instagram}
        youtube={formData.youtube}
        onFacebookChange={(value) => updateField("facebook", value)}
        onInstagramChange={(value) => updateField("instagram", value)}
        onYoutubeChange={(value) => updateField("youtube", value)}
      />

      <ProfileSlugSection
        handle={formData.handle}
        onHandleChange={handleHandleChange}
        error={displayHandleError}
        showError={showHandleError || Boolean(displayHandleError)}
      />

      {submitting ? (
        <Loader />
      ) : (
        <SubmitSection
          onboardingMode={onboardingMode}
          disabled={photoCropActive}
        />
      )}
    </form>
  );
}

export default EditProfileForm;
